/* Theme-aware background painter.
 *
 * Replaces the old matrix-rain fire-and-forget rAF loop with the
 * altsolar pattern (Lopettia/clientes/altsolar/demos-site themes.js):
 *
 *   - One canvas id="matrix-rain" (kept for HTML compat — see CLAUDE.md
 *     note: every entry HTML still has `<canvas id="matrix-rain">`).
 *   - Each theme owns ITS OWN painter. The previous painter's RAF loop
 *     is cancelled before a new one starts — no more wasted CPU on
 *     hidden green code.
 *   - prefers-reduced-motion: paint once, stop.
 *   - Listens to `lp-theme-changed` and re-paints accordingly.
 *
 * Painters:
 *   matrix    → green code rain (the original)
 *   control   → spy radar sweep with blip dots (Agente 86)
 *   editorial → static paper grid (no canvas needed — CSS does it)
 *   boardroom → drifting blue particle haze
 */

import { getCurrentTheme, type ThemeId } from './theme-picker';

type Painter = (
  ctx: CanvasRenderingContext2D,
  w: () => number,
  h: () => number,
  reduced: boolean,
  isStopped: () => boolean,
  setRaf: (id: number) => void,
) => void;

let _stopCurrent: (() => void) | null = null;

/** Public entry: bind to a theme and start painting. Theme changes
 *  cancel the current painter and start the new one. */
export function attachBackgroundPainter(): void {
  const canvas = document.getElementById('matrix-rain') as HTMLCanvasElement | null;
  if (!canvas) return;

  // Resize handler is shared across painters — set up once, painters
  // just read window.innerWidth/Height.
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  function resize() {
    const dpr = window.devicePixelRatio || 1;
    canvas!.width = window.innerWidth * dpr;
    canvas!.height = window.innerHeight * dpr;
    canvas!.style.width = window.innerWidth + 'px';
    canvas!.style.height = window.innerHeight + 'px';
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();
  window.addEventListener('resize', resize);

  paint(getCurrentTheme(), ctx);

  window.addEventListener('lp-theme-changed', (e: Event) => {
    const ce = e as CustomEvent<{ theme: ThemeId }>;
    paint(ce.detail?.theme || getCurrentTheme(), ctx);
  });
}

function paint(theme: ThemeId, ctx: CanvasRenderingContext2D): void {
  // Cancel whatever was running.
  if (_stopCurrent) {
    _stopCurrent();
    _stopCurrent = null;
  }
  // Clear the canvas so old strokes don't linger across the swap.
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  let stopped = false;
  let rafId = 0;
  const isStopped = () => stopped;
  const setRaf = (id: number) => { rafId = id; };
  _stopCurrent = () => {
    stopped = true;
    if (rafId) cancelAnimationFrame(rafId);
  };

  const w = () => window.innerWidth;
  const h = () => window.innerHeight;

  const painter = PAINTERS[theme] ?? PAINTERS.matrix;
  painter(ctx, w, h, reduced, isStopped, setRaf);
}

// ============================================================================
// Painters
// ============================================================================

const MATRIX_CHARSET =
  'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ<>{}[]/=+-*&%$#@!?'.split('');

const matrixPainter: Painter = (ctx, w, h, reduced, isStopped, setRaf) => {
  let cols = 0;
  let drops: number[] = [];
  let fontSize = 14;

  function setup() {
    fontSize = Math.max(12, Math.min(16, Math.floor(w() / 90)));
    cols = Math.floor(w() / fontSize);
    drops = new Array(cols).fill(0).map(() => Math.random() * -100);
  }
  setup();
  // Resize event — only this painter cares about cols/fontSize recompute.
  const onResize = () => setup();
  window.addEventListener('resize', onResize);

  function frame() {
    if (isStopped()) {
      window.removeEventListener('resize', onResize);
      return;
    }
    ctx.fillStyle = 'rgba(7, 11, 20, 0.045)';
    ctx.fillRect(0, 0, w(), h());
    ctx.font = `bold ${fontSize}px 'JetBrains Mono', monospace`;
    for (let i = 0; i < cols; i++) {
      const ch = MATRIX_CHARSET[Math.floor(Math.random() * MATRIX_CHARSET.length)];
      const x = i * fontSize;
      const y = drops[i] * fontSize;
      ctx.shadowColor = '#7FFFD4'; ctx.shadowBlur = 8;
      ctx.fillStyle = 'rgba(220, 255, 235, 1.0)';
      ctx.fillText(ch, x, y);
      ctx.shadowBlur = 4;
      ctx.fillStyle = 'rgba(0, 255, 156, 0.95)';
      if (drops[i] > 1) ctx.fillText(MATRIX_CHARSET[Math.floor(Math.random() * MATRIX_CHARSET.length)], x, y - fontSize);
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(0, 255, 156, 0.55)';
      if (drops[i] > 2) ctx.fillText(MATRIX_CHARSET[Math.floor(Math.random() * MATRIX_CHARSET.length)], x, y - fontSize * 2);
      if (y > h() && Math.random() > 0.975) drops[i] = 0;
      drops[i] += 1;
    }
    ctx.shadowBlur = 0;
    if (!reduced) setRaf(requestAnimationFrame(frame));
  }
  frame();
};

/** CONTROL — Agente 86 radar sweep over concentric range rings + blips. */
const controlPainter: Painter = (ctx, w, h, reduced, isStopped, setRaf) => {
  let angle = 0;
  const RING_COUNT = 5;
  const BLIPS = Array.from({ length: 9 }, () => ({
    angle: Math.random() * Math.PI * 2,
    dist: 0.15 + Math.random() * 0.7,   // fraction of max radius
    life: Math.random(),
  }));

  function frame() {
    if (isStopped()) return;

    const cx = w() / 2;
    const cy = h() / 2;
    const maxR = Math.max(w(), h()) * 0.65;

    // Fade prior frame slightly (no full clear — leaves a trail on the sweep)
    ctx.fillStyle = 'rgba(10, 24, 50, 0.10)';
    ctx.fillRect(0, 0, w(), h());

    // Concentric range rings
    ctx.strokeStyle = 'rgba(212, 168, 87, 0.10)';   // brass-tinted
    ctx.lineWidth = 1;
    for (let i = 1; i <= RING_COUNT; i++) {
      ctx.beginPath();
      ctx.arc(cx, cy, (maxR / RING_COUNT) * i, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Cross-hair
    ctx.beginPath();
    ctx.moveTo(cx, cy - maxR); ctx.lineTo(cx, cy + maxR);
    ctx.moveTo(cx - maxR, cy); ctx.lineTo(cx + maxR, cy);
    ctx.stroke();

    // Sweep wedge — phone-booth red with falloff
    const sweepAngle = Math.PI / 5;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR);
    grad.addColorStop(0, 'rgba(196, 30, 58, 0.0)');
    grad.addColorStop(0.6, 'rgba(196, 30, 58, 0.10)');
    grad.addColorStop(1.0, 'rgba(196, 30, 58, 0.22)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, maxR, angle - sweepAngle, angle);
    ctx.closePath();
    ctx.fill();

    // Bright leading edge
    ctx.strokeStyle = 'rgba(196, 30, 58, 0.55)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
    ctx.stroke();

    // Blips — brass dots that flare when the sweep passes them
    BLIPS.forEach((b) => {
      const r = b.dist * maxR;
      const bx = cx + Math.cos(b.angle) * r;
      const by = cy + Math.sin(b.angle) * r;
      // Distance in radians from sweep edge (negative = ahead, positive = behind)
      let delta = angle - b.angle;
      delta = ((delta % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const hot = delta < 0.6 ? 1 - delta / 0.6 : 0;
      const alpha = 0.18 + 0.7 * hot;
      ctx.fillStyle = `rgba(212, 168, 87, ${alpha})`;
      ctx.beginPath();
      ctx.arc(bx, by, 2 + 3 * hot, 0, Math.PI * 2);
      ctx.fill();
    });

    angle += 0.012;   // ~one revolution every ~9 seconds at 60fps
    if (angle > Math.PI * 2) angle -= Math.PI * 2;

    if (!reduced) setRaf(requestAnimationFrame(frame));
  }
  frame();
};

/** EDITORIAL — paper rest. No canvas animation; CSS adds the grid. */
const editorialPainter: Painter = (ctx) => {
  // Static cream wash. Canvas left blank; the underlying body bg + CSS
  // grid pattern do the visual work.
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
};

/** BOARDROOM — slow drifting blue particles. */
const boardroomPainter: Painter = (ctx, w, h, reduced, isStopped, setRaf) => {
  const PARTICLES = Array.from({ length: 70 }, () => ({
    x: Math.random() * w(),
    y: Math.random() * h(),
    vx: (Math.random() - 0.5) * 0.2,
    vy: (Math.random() - 0.5) * 0.2,
    r: 0.7 + Math.random() * 1.6,
    alpha: 0.18 + Math.random() * 0.42,
  }));

  function frame() {
    if (isStopped()) return;
    // Soft trail
    ctx.fillStyle = 'rgba(15, 27, 45, 0.18)';
    ctx.fillRect(0, 0, w(), h());

    PARTICLES.forEach((p) => {
      p.x += p.vx; p.y += p.vy;
      if (p.x < 0) p.x = w();
      if (p.x > w()) p.x = 0;
      if (p.y < 0) p.y = h();
      if (p.y > h()) p.y = 0;
      ctx.fillStyle = `rgba(133, 193, 233, ${p.alpha})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    });

    if (!reduced) setRaf(requestAnimationFrame(frame));
  }
  frame();
};

const PAINTERS: Record<ThemeId, Painter> = {
  matrix:    matrixPainter,
  control:   controlPainter,
  editorial: editorialPainter,
  boardroom: boardroomPainter,
};
